"use client"

import type React from "react"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Calculator, Loader2, ChevronDown } from "lucide-react"
import { SICCodeLookup } from "./sic-code-lookup"
import { ErrorAlert } from "@/components/ui/error-alert"
import { dlomApiService, type ProcessedDLOMResults } from "@/lib/api-service"
import { YearMonthPicker } from "@/components/ui/year-month-picker"
import { yearMonthSchema } from "@/lib/date-validation"

interface SimpleCalculatorFormProps {
  onResultsCalculated?: (results: ProcessedDLOMResults) => void
}

export function SimpleCalculatorForm({ onResultsCalculated }: SimpleCalculatorFormProps) {
  const [isCalculating, setIsCalculating] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [showUserInputMode, setShowUserInputMode] = useState(false)
  const [userInputEnabled, setUserInputEnabled] = useState(false)
  
  const simpleSchema = z.object({
    valuationDate: yearMonthSchema,
    marketValue: z
      .number()
      .positive("Market value must be greater than 0"),
    blockSize: z
      .number()
      .min(0, "Block Size must be between 0 and 100")
      .max(100, "Block Size must be between 0 and 100"),
    sic2d: z
      .string()
      .min(1, "SIC code is required")
      .regex(/^\d{2}$/, "SIC code must be 2 digits")
      .refine((v) => {
        const sic = parseInt(v, 10)
        return sic >= 1 && sic <= 99
      }, "SIC code must be between 01 and 99"),
    // Optional User Input Mode overrides
    uiSigQ1: z.number().positive().optional(),
    uiSigMedian: z.number().positive().optional(),
    uiSigQ3: z.number().positive().optional(),
    uiHlifeQ1: z.number().positive().optional(),
    uiHlifeMedian: z.number().positive().optional(),
    uiHlifeQ3: z.number().positive().optional(),
  })

  type SimpleFormData = z.infer<typeof simpleSchema>

  const form = useForm<SimpleFormData>({
    resolver: zodResolver(simpleSchema),
    defaultValues: {
      valuationDate: "2024-12",
      marketValue: 10000000,
      blockSize: 50,
      sic2d: "", // No default value since it's now required
      uiSigQ1: undefined,
      uiSigMedian: undefined,
      uiSigQ3: undefined,
      uiHlifeQ1: undefined,
      uiHlifeMedian: undefined,
      uiHlifeQ3: undefined,
    },
  })

  const handleSICCodeSelect = (code: string) => {
    const twoDigitCode = code.substring(0, 2)
    form.setValue("sic2d", twoDigitCode)
  }

  const onSubmit = async (data: SimpleFormData) => {
    setIsCalculating(true)
    setError(null)
    try {
      const rid = data.valuationDate // already YYYY-MM from month picker
      const results = await dlomApiService.calculateDLOM({
        BlockSize: data.blockSize,
        RID: rid,
        MVAL: data.marketValue,
        SIC: parseInt(data.sic2d, 10),
        UISigQ1: userInputEnabled ? data.uiSigQ1 : undefined,
        UISigMedian: userInputEnabled ? data.uiSigMedian : undefined,
        UISigQ3: userInputEnabled ? data.uiSigQ3 : undefined,
        UIhlifeQ1: userInputEnabled ? data.uiHlifeQ1 : undefined,
        UIhlifeMedian: userInputEnabled ? data.uiHlifeMedian : undefined,
        UIhlifeQ3: userInputEnabled ? data.uiHlifeQ3 : undefined,
      })
      onResultsCalculated?.(results)
    } catch (err) {
      console.error("Calculation failed", err)
      setError(err instanceof Error ? err : new Error("An unexpected error occurred"))
    } finally {
      setIsCalculating(false)
    }
  }

  const dismissError = () => {
    setError(null)
  }

  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-6 border-b">
        <CardTitle className="text-xl font-semibold flex items-center">
          <Calculator className="h-5 w-5 mr-3 text-primary" />
          Parameters
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">Configure your calculation inputs</p>
      </CardHeader>
      <CardContent className="p-6 space-y-6">
        {error && (
          <ErrorAlert 
            error={error} 
            onDismiss={dismissError}
            className="mb-4"
          />
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            <FormField
              control={form.control}
              name="valuationDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Valuation Date <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <YearMonthPicker
                      placeholder="Select valuation month"
                      required
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    The date for which the discount calculation is performed. Must be between 2005-01 and 2024-12.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="marketValue"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Market Value ($) <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="Enter market value"
                      {...field}
                      required
                      onChange={(e) => field.onChange(Number.parseFloat(e.target.value) || 0)}
                    />
                  </FormControl>
                  <FormDescription>Pre-discount market value of the subject company</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="blockSize"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Block Size (%) <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      step={1}
                      placeholder="Enter block size percentage"
                      {...field}
                      required
                      onChange={(e) => field.onChange(Number.parseFloat(e.target.value))}
                    />
                  </FormControl>
                  <FormDescription>Percentage of shares represented by the block (0–100).</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="sic2d"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Industry <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <div className="flex items-center gap-2">
                      <Input
                        placeholder="Enter 2-digit SIC code (01-99)"
                        value={field.value ?? ""}
                        onChange={field.onChange}
                        required
                      />
                      <SICCodeLookup onSelect={handleSICCodeSelect} />
                    </div>
                  </FormControl>
                  <FormDescription>Enter a 2-digit SIC code (01–99) or use the lookup to select your industry.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="pt-2">
              <button
                type="button"
                onClick={() => setShowUserInputMode((v) => {
                  const next = !v
                  if (!next) {
                    setUserInputEnabled(false)
                  }
                  return next
                })}
                className="w-full flex items-center justify-between rounded-md border px-3 py-2 text-left hover:bg-accent hover:text-accent-foreground"
                aria-expanded={showUserInputMode}
                aria-controls="user-input-mode-section"
              >
                <span className="text-sm font-medium">User Input Mode (optional)</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${showUserInputMode ? "rotate-180" : "rotate-0"}`} />
              </button>

              {showUserInputMode && (
                <div id="user-input-mode-section" className="mt-3">
                  <div className="flex items-center gap-2 mb-3">
                    <Checkbox id="enable-user-input" checked={userInputEnabled} onCheckedChange={(c) => setUserInputEnabled(Boolean(c))} />
                    <Label htmlFor="enable-user-input">Enable User Input Mode</Label>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">Provide custom volatility and liquidation period values to generate User Input Mode tables.</p>

                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-1/3">Parameter</TableHead>
                          <TableHead className="text-center">Low (25th Percentile)</TableHead>
                          <TableHead className="text-center">Median (50th Percentile)</TableHead>
                          <TableHead className="text-center">High (75th Percentile)</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell className="font-medium">Annualized Std Dev (%)</TableCell>
                          <TableCell>
                            <FormField
                              control={form.control}
                              name="uiSigQ1"
                              render={({ field }) => (
                                <FormItem>
                                  <FormControl>
                                    <Input
                                      type="number"
                                      placeholder="e.g., 15.5"
                                      value={field.value ?? ""}
                                      disabled={!userInputEnabled}
                                      onChange={(e) => field.onChange(e.target.value === "" ? undefined : Number.parseFloat(e.target.value))}
                                      className="text-center"
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </TableCell>
                          <TableCell>
                            <FormField
                              control={form.control}
                              name="uiSigMedian"
                              render={({ field }) => (
                                <FormItem>
                                  <FormControl>
                                    <Input
                                      type="number"
                                      placeholder="e.g., 20.0"
                                      value={field.value ?? ""}
                                      disabled={!userInputEnabled}
                                      onChange={(e) => field.onChange(e.target.value === "" ? undefined : Number.parseFloat(e.target.value))}
                                      className="text-center"
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </TableCell>
                          <TableCell>
                            <FormField
                              control={form.control}
                              name="uiSigQ3"
                              render={({ field }) => (
                                <FormItem>
                                  <FormControl>
                                    <Input
                                      type="number"
                                      placeholder="e.g., 30.0"
                                      value={field.value ?? ""}
                                      disabled={!userInputEnabled}
                                      onChange={(e) => field.onChange(e.target.value === "" ? undefined : Number.parseFloat(e.target.value))}
                                      className="text-center"
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium">Liquidation Period (days)</TableCell>
                          <TableCell>
                            <FormField
                              control={form.control}
                              name="uiHlifeQ1"
                              render={({ field }) => (
                                <FormItem>
                                  <FormControl>
                                    <Input
                                      type="number"
                                      placeholder="e.g., 45.23"
                                      value={field.value ?? ""}
                                      disabled={!userInputEnabled}
                                      onChange={(e) => field.onChange(e.target.value === "" ? undefined : Number.parseFloat(e.target.value))}
                                      className="text-center"
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </TableCell>
                          <TableCell>
                            <FormField
                              control={form.control}
                              name="uiHlifeMedian"
                              render={({ field }) => (
                                <FormItem>
                                  <FormControl>
                                    <Input
                                      type="number"
                                      placeholder="e.g., 100.00"
                                      value={field.value ?? ""}
                                      disabled={!userInputEnabled}
                                      onChange={(e) => field.onChange(e.target.value === "" ? undefined : Number.parseFloat(e.target.value))}
                                      className="text-center"
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </TableCell>
                          <TableCell>
                            <FormField
                              control={form.control}
                              name="uiHlifeQ3"
                              render={({ field }) => (
                                <FormItem>
                                  <FormControl>
                                    <Input
                                      type="number"
                                      placeholder="e.g., 250.75"
                                      value={field.value ?? ""}
                                      disabled={!userInputEnabled}
                                      onChange={(e) => field.onChange(e.target.value === "" ? undefined : Number.parseFloat(e.target.value))}
                                      className="text-center"
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>

            <Button 
              type="submit" 
              className="w-full font-semibold shadow-sm" 
              size="lg" 
              disabled={isCalculating}
            >
              {isCalculating ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Calculating...
                </>
              ) : (
                <>
                  <Calculator className="h-5 w-5 mr-2" />
                  Calculate Discount
                </>
              )}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}
